import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, FileText, ScrollText, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Stats {
  clients: number;
  frameworks: number;
  tocs: number;
}

interface RecentToc {
  id: string;
  focus_keyword: string;
  created_at: string;
  client_name: string;
  framework_name: string;
}

const ContentLabDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats>({ clients: 0, frameworks: 0, tocs: 0 });
  const [recent, setRecent] = useState<RecentToc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, frameworksRes, tocsRes, recentRes] = await Promise.all([
          supabase.from('content_lab_clients').select('id', { count: 'exact', head: true }),
          supabase.from('content_lab_frameworks').select('id', { count: 'exact', head: true }),
          supabase.from('content_lab_tocs').select('id', { count: 'exact', head: true }),
          supabase.from('content_lab_tocs')
            .select('id, focus_keyword, created_at, client_id, framework_id')
            .order('created_at', { ascending: false })
            .limit(5),
        ]);

        setStats({
          clients: clientsRes.count || 0,
          frameworks: frameworksRes.count || 0,
          tocs: tocsRes.count || 0,
        });

        if (recentRes.data && recentRes.data.length > 0) {
          // Fetch client and framework names for recent tocs
          const clientIds = [...new Set(recentRes.data.map(t => t.client_id))];
          const frameworkIds = [...new Set(recentRes.data.map(t => t.framework_id))];

          const [clientsData, frameworksData] = await Promise.all([
            supabase.from('content_lab_clients').select('id, name').in('id', clientIds),
            supabase.from('content_lab_frameworks').select('id, framework_name').in('id', frameworkIds),
          ]);

          const clientMap = Object.fromEntries((clientsData.data || []).map(c => [c.id, c.name]));
          const frameworkMap = Object.fromEntries((frameworksData.data || []).map(f => [f.id, f.framework_name]));

          setRecent(recentRes.data.map(t => ({
            id: t.id,
            focus_keyword: t.focus_keyword,
            created_at: t.created_at,
            client_name: clientMap[t.client_id] || 'Unknown',
            framework_name: frameworkMap[t.framework_id] || 'Unknown',
          })));
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { label: 'Total Clients', value: stats.clients, icon: Users, color: 'text-blue-500' },
    { label: 'Total Frameworks', value: stats.frameworks, icon: FileText, color: 'text-green-500' },
    { label: 'TOCs Generated', value: stats.tocs, icon: ScrollText, color: 'text-purple-500' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Content Lab</h1>
        <p className="text-muted-foreground mt-1">Generate structured content guides with AI</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label}>
            <CardContent className="flex items-center gap-4 p-6">
              <div className={`p-3 rounded-lg bg-muted ${color}`}>
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-2xl font-bold">{loading ? '—' : value}</p>
                <p className="text-sm text-muted-foreground">{label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Generations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Recent Generations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : recent.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ScrollText className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p>No TOCs generated yet</p>
              <p className="text-sm mt-1">Go to Generate to create your first content guide</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recent.map(toc => (
                <div
                  key={toc.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/content-lab/history`)}
                >
                  <div>
                    <p className="font-medium">{toc.focus_keyword}</p>
                    <p className="text-sm text-muted-foreground">
                      {toc.client_name} · {toc.framework_name}
                    </p>
                  </div>
                  <Badge variant="secondary">
                    {new Date(toc.created_at).toLocaleDateString()}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContentLabDashboard;
